import React, { useRef, useState } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  CONTENT_BLOCK_HEIGHT,
  externalFlowContentColumnSx,
  CARD_INSET_HORIZONTAL_PX,
  externalFlowGlassCardLayoutSx,
  externalFlowGlassSurfaceSx,
  externalFlowInCardBrandingSx,
  externalFlowPrimaryButtonSx,
  externalFlowRequirementSurfaceSx,
  externalFlowSecondaryTextButtonSx,
  externalFlowLineClamp3Sx,
  FLOW_MB_AFTER_ICON,
  FLOW_MB_AFTER_SUBTITLE,
  FLOW_MB_AFTER_TITLE,
  LABEL_PRIMARY,
  LABEL_SECONDARY,
  LETTER_SPACING,
  REQUIREMENT_CTA_STRIP_HEIGHT,
  REQUIREMENT_SUBTITLE_FONT_SIZE,
  REQUIREMENT_TEXT_TOP_PX,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UPLOAD_FLOW_PAGE_BG,
  requirementCtaPrimaryButtonSx,
  requirementCtaStripSx,
  requirementIllustrationSx,
} from './uploadCardLayout';

const isUnsupportedFileType = (errorType) =>
  errorType === 'INVALID_FILE_TYPE' ||
  errorType === 'INVALID_TYPE' ||
  errorType === 'UNSUPPORTED_MEDIA_TYPE' ||
  errorType === 'MULTI_PAGE_PDF';

const isAnalyzeFailure = (errorType) =>
  errorType === 'NO_DIMENSIONS' ||
  errorType === 'MIXED_SCALE' ||
  errorType === 'SIZE_UNSUPPORTED' ||
  errorType === 'NOT_A_FLOOR_PLAN' ||
  errorType === 'FILE_UNREADABLE' ||
  errorType === 'INVALID_PDF' ||
  errorType === 'FURNITURE_DETECTED' ||
  errorType === 'NO_USABLE_ROOMS' ||
  errorType === 'VALIDATION_ERROR' ||
  errorType === 'JOB_FAILED';

const ErrorCard = ({ errorType, errorMessage, onFileSelect, onSeeWhatWorks }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const unsupported = isUnsupportedFileType(errorType);
  const analyzeFailure = isAnalyzeFailure(errorType);

  const getErrorContent = () => {
    switch (errorType) {
      // Unsupported file type
      case 'INVALID_FILE_TYPE':
      case 'INVALID_TYPE':
      case 'UNSUPPORTED_MEDIA_TYPE':
      case 'MULTI_PAGE_PDF':
        return {
          title: "This file type isn't supported",
          subtitle: 'Please upload a JPG or PNG',
        };

      // Analysis / content rejection — keep copy honest; we are not asserting
      // a definitive "not a floor plan" diagnosis when heuristics are involved.
      case 'NO_DIMENSIONS':
      case 'MIXED_SCALE':
      case 'SIZE_UNSUPPORTED':
      case 'NOT_A_FLOOR_PLAN':
      case 'FILE_UNREADABLE':
      case 'INVALID_PDF':
      case 'FURNITURE_DETECTED':
      case 'NO_USABLE_ROOMS':
      case 'VALIDATION_ERROR':
      case 'JOB_FAILED':
        return {
          title: "We couldn't analyze this plan",
          subtitle: 'Likely a resolution, labeling, or scale issue',
        };

      // File too large
      case 'FILE_TOO_LARGE':
      case 'PLAN_SIZE_UNSUPPORTED':
      case 'PAYLOAD_TOO_LARGE':
        return {
          title: 'File too large',
          subtitle: 'Please upload a file under 10 MB',
        };

      // Server-side / backend failures — do not blame the user
      case 'NETWORK_ERROR':
      case 'TIMEOUT_ERROR':
      case 'SERVICE_UNAVAILABLE':
      case 'RATE_LIMIT':
      case 'FORBIDDEN':
      case 'POLLING_TIMEOUT':
      case 'SERVER_ERROR':
        return {
          title: 'Something went wrong on our end',
          subtitle: 'Please try again in a moment',
        };

      // Default catch-all
      case 'NO_FILE':
      case 'UNKNOWN_ERROR':
      default:
        return {
          title: 'Something went wrong',
          subtitle: 'Please try again or upload a different floor plan',
        };
    }
  };

  const { title, subtitle } = getErrorContent();

  const acceptFile = (file) => {
    if (file && onFileSelect) onFileSelect(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    acceptFile(file);
    // Allow re-selecting the same file after another failure.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    acceptFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const related = event.relatedTarget;
    if (!related || !event.currentTarget.contains(related)) {
      setIsDragging(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        flexShrink: 0,
        bgcolor: UPLOAD_FLOW_PAGE_BG,
        width: '100%',
      }}
    >
      <Box
        sx={{
          ...externalFlowContentColumnSx,
          minHeight: CONTENT_BLOCK_HEIGHT,
          maxHeight: CONTENT_BLOCK_HEIGHT,
        }}
      >
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="region"
          aria-label="Drop zone to upload another floor plan"
          sx={{
            ...externalFlowGlassCardLayoutSx,
            ...(unsupported
              ? {
                  ...externalFlowRequirementSurfaceSx,
                  // Full-bleed graphic + strip nested to card edge (text keeps inset).
                  px: 0,
                  pt: 0,
                  pb: 0,
                }
              : externalFlowGlassSurfaceSx),
          }}
        >
          {isDragging && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.18)',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          )}

        {unsupported && (
          <Box
            component="img"
            src="/assets/requirement-0.svg"
            alt="Unsupported PDF and SVG versus supported JPG and PNG"
            draggable={false}
            sx={requirementIllustrationSx}
          />
        )}

          <Stack
          spacing={0}
          alignItems="center"
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            zIndex: 3,
            boxSizing: 'border-box',
            // Match requirements guide: illustration flush top, text at shared Y.
            ...(unsupported
              ? {
                  px: `${CARD_INSET_HORIZONTAL_PX}px`,
                  pt: `${REQUIREMENT_TEXT_TOP_PX}px`,
                  pb: `${REQUIREMENT_CTA_STRIP_HEIGHT}px`,
                }
              : null),
          }}
        >
          {!unsupported && <Box sx={{ flex: 1, minHeight: 0 }} />}

          {!unsupported && (
            <Box
              component="img"
              src="/assets/upload-error.svg"
              alt=""
              aria-hidden
              draggable={false}
              sx={{
                // Same footprint as the previous 36×36 error icon well.
                width: 36,
                height: 36,
                display: 'block',
                flexShrink: 0,
                mb: FLOW_MB_AFTER_ICON,
                userSelect: 'none',
              }}
            />
          )}

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: LABEL_PRIMARY,
              fontSize: TITLE_FONT_SIZE,
              letterSpacing: LETTER_SPACING,
              lineHeight: 1.2,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              px: 0.5,
              mb: unsupported ? 0.35 : FLOW_MB_AFTER_TITLE,
              ...(unsupported ? null : externalFlowLineClamp3Sx),
            }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={
              unsupported
                ? {
                    color: LABEL_SECONDARY,
                    fontSize: REQUIREMENT_SUBTITLE_FONT_SIZE,
                    letterSpacing: LETTER_SPACING,
                    lineHeight: 1.35,
                    textAlign: 'center',
                    maxWidth: 280,
                    whiteSpace: 'nowrap',
                  }
                : {
                    // Match idle / uploading / done subtitle token (was maxWidth 235 → wrap).
                    color: LABEL_SECONDARY,
                    fontSize: SUBTITLE_FONT_SIZE,
                    letterSpacing: LETTER_SPACING,
                    lineHeight: 1.4,
                    textAlign: 'center',
                    width: '100%',
                    maxWidth: '100%',
                    mb: FLOW_MB_AFTER_SUBTITLE,
                    ...externalFlowLineClamp3Sx,
                  }
            }
          >
            {subtitle}
          </Typography>

          {/* Inline CTAs for non-requirement error cards only */}
          {!unsupported && analyzeFailure && onSeeWhatWorks ? (
            // Click path goes through the requirements guide; drag-and-drop
            // still accepts a replacement file on this card.
            <Button
              variant="contained"
              size="medium"
              onClick={onSeeWhatWorks}
              aria-label="Is my plan compatible?"
              disableElevation
              sx={{
                ...externalFlowPrimaryButtonSx,
                whiteSpace: 'nowrap',
                ...(isDragging && { background: 'rgba(255,255,255,0.55)' }),
              }}
            >
              Is my plan compatible?
            </Button>
          ) : !unsupported && onFileSelect ? (
            <Button
              variant="contained"
              size="medium"
              startIcon={<FileUploadOutlinedIcon sx={{ fontSize: '20px !important' }} />}
              onClick={handleBrowseClick}
              aria-label="Upload another floor plan"
              disableElevation
              sx={{
                ...externalFlowPrimaryButtonSx,
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': { ml: -0.25, mr: 1 },
                ...(isDragging && { background: 'rgba(255,255,255,0.55)' }),
              }}
            >
              Upload another
            </Button>
          ) : null}

          {!analyzeFailure && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          )}

          {!unsupported && <Box sx={{ flex: 1, minHeight: 0 }} />}

          {!unsupported && (
            <Typography sx={externalFlowInCardBrandingSx}>
              Powered by MagicFurnish
            </Typography>
          )}
        </Stack>

        {unsupported && onFileSelect && (
          <Box sx={requirementCtaStripSx}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={onSeeWhatWorks}
                aria-label="Is my plan compatible?"
                sx={externalFlowSecondaryTextButtonSx}
              >
                Is my plan compatible?
              </Button>
            </Box>
            <Box />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Button
                variant="contained"
                size="medium"
                onClick={handleBrowseClick}
                aria-label="Upload another floor plan"
                disableElevation
                sx={{
                  ...requirementCtaPrimaryButtonSx,
                  ...(isDragging && { background: 'rgba(255,255,255,0.55)' }),
                }}
              >
                Upload another
              </Button>
            </Box>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};

export default ErrorCard;
